import { Navigate, useSearchParams } from "react-router";

function getRedirectParams(searchParams: URLSearchParams) {
    const params = new URLSearchParams(searchParams);
    params.delete("page");
    return params;
}

function handleDashboardProductPage(sanitizedParams: URLSearchParams) {
    const params = new URLSearchParams(sanitizedParams);
    const codename = params.get("codename");
    const version = params.get("version");
    params.delete("codename");
    params.delete("version");

    if (codename && version) {
        const encodedCodename = encodeURIComponent(codename);
        const encodedVersion = encodeURIComponent(version);

        return (
            <Navigate to={"/dashboard/products/" + encodedCodename + "/" + encodedVersion + "?" + params.toString()} replace />
        );
    }

    return <Navigate to={"/dashboard/products?" + params.toString()} replace />;
}

function handlePasswordChangePage(sanitizedParams: URLSearchParams) {
    const params = new URLSearchParams(sanitizedParams);
    params.set("mode", "password");
    return <Navigate to={"/account/settings/security?" + params.toString()} replace />;
}

export const pageHandlers = {
    accountConfirmation: (params: URLSearchParams) => <Navigate to={"/account/confirm?" + params.toString()} replace />,
    accountSecurity: (params: URLSearchParams) => <Navigate to={"/account/settings/security?" + params.toString()} replace />,
    invitation: (params: URLSearchParams) => <Navigate to={"/invitation?" + params.toString()} replace />,
    login: (params: URLSearchParams) => <Navigate to={"/login?" + params.toString()} replace />,
    logout: (params: URLSearchParams) => <Navigate to={"/logout?" + params.toString()} replace />,
    passwordChange: handlePasswordChangePage,
    passwordRecover: (params: URLSearchParams) => <Navigate to={"/account/recover?" + params.toString()} replace />,
    passwordReset: (params: URLSearchParams) => <Navigate to={"/account/reset?" + params.toString()} replace />,
    product: handleDashboardProductPage,
} satisfies Record<string, (params: URLSearchParams) => React.JSX.Element>;

type PageParam = keyof typeof pageHandlers;

function isPageParam(page: string): page is PageParam {
    return Object.hasOwn(pageHandlers, page);
}

export const RedirectService = () => {
    const [searchParams] = useSearchParams();
    const sanitizedParams = getRedirectParams(searchParams);

    const page = searchParams.get("page");
    const handler = page && isPageParam(page) ? pageHandlers[page] : undefined;

    return handler ? handler(sanitizedParams) : <Navigate to="/" replace />;
};
